import React, { useState, useEffect, useRef } from "react";
import "./orderFilter.css";

const OrderFilter = ({
  setQuery,
  onAddManualOrder,
  initialQuery = {},
  sourceTab = "importerr",
  onSourceTabChange,
}) => {
  const [filterOrder, setFilterOrder] = useState({
    searchFields: "",
    startDate: "",
    endDate: "",
    state: "",
    city: "",
    status: initialQuery.status || "",
    ticket: "",
    paymentStatus: "",
    returnStatus: "",
    marketplace: "",
    orderType: "",
    fromFailedOrder: "",
    orderExport: "",
    bankTransferConfirmOnly: "",
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  
  const filterRef = useRef(filterOrder);
  filterRef.current = filterOrder;

  // Order Status options
  const statusOptions = [
    { name: "All Status", value: "" },
    { name: "Abnormal", value: "abnormal" },
    { name: "Payment Pending", value: "payment pending" },
    { name: "Payment Paid", value: "payment paid" },
    { name: "Processing", value: "processing" },
    { name: "Order Processed to Supplier", value: "order processed to supplier" },
    { name: "Partially Shipped", value: "partially-shipped" },
    { name: "Shipped", value: "shipped" },
    { name: "Delivered", value: "delivered" },
    { name: "Cancelled", value: "cancel" },
    { name: "Refunded", value: "refunded" },
  ];

  // Payment Status (MKP) — values sent as query.paymentStatus; matches advanced filter + backend
  const paymentOptions = [
    { name: "All", value: "" },
    { name: "Paid", value: "paid" },
    { name: "Pending", value: "pending" },
    { name: "Failed", value: "failed" },
  ];

  // Ticket options
  const ticketOptions = [
    { name: "All Tickets", value: "" },
    { name: "Has Ticket", value: "true" },
    { name: "No Ticket", value: "false" },
  ];

  // Marketplace options
  const marketplaceOptions = [
    { name: "All Marketplaces", value: "" },
    { name: "Shopify", value: "shopify" },
    { name: "Amazon", value: "amazon" },
    { name: "Manual", value: "manual" },
  ];

  // Order Type options
  const orderTypeOptions = [
    { name: "All Types", value: "" },
    { name: "Standard", value: "standard" },
    { name: "Express", value: "express" },
    { name: "COD", value: "cod" },
  ];

  // Failed Order options
  const failedOrderOptions = [
    { name: "All Orders", value: "" },
    { name: "From Failed Orders", value: "true" },
    { name: "Regular Orders", value: "false" },
  ];

  // Export Status options
  const exportOptions = [
    { name: "All", value: "" },
    { name: "Exported", value: "true" },
    { name: "Not Exported", value: "false" },
  ];

  // Return Status options
  const returnOptions = [
    { name: "All Returns", value: "" },
    { name: "RTO", value: "rto" },
    { name: "Customer Support", value: "customerSupport" },
  ];

  // Count active filters
  useEffect(() => {
    const count = Object.values(filterOrder).filter(val => val !== "").length;
    setActiveFiltersCount(count);
  }, [filterOrder]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterOrder(prev => ({ ...prev, [name]: value }));
  };

  const handleFilter = () => {
    setQuery({ ...filterRef.current });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleFilter();
    }
  };

  const clearFilter = () => {
    const clearedFilter = {
      searchFields: "",
      startDate: "",
      endDate: "",
      state: "",
      city: "",
      status: "",
      ticket: "",
      paymentStatus: "",
      returnStatus: "",
      marketplace: "",
      orderType: "",
      fromFailedOrder: "",
      orderExport: "",
      bankTransferConfirmOnly: "",
    };
    setFilterOrder(clearedFilter);
    setQuery(clearedFilter);
  };

  // Quick filter buttons (status filters + Tickets filter)
  const quickFilters = [
    { label: "Pending", value: "payment pending", icon: "🕐" },
    { label: "Processing", value: "processing", icon: "⚙️" },
    { label: "Shipped", value: "shipped", icon: "🚚" },
    { label: "Delivered", value: "delivered", icon: "✅" },
    { label: "Cancelled", value: "cancel", icon: "❌" },
    { label: "Abnormal", value: "abnormal", icon: "⚠️" },
    { label: "Confirm Bank Transfer", value: "__confirm_bt__", icon: "🏦", confirmBankTransferFilter: true },
    { label: "Tickets", value: "__ticket__", icon: "🎫", ticketFilter: true },
  ];
  const sourceTabs = [
    { label: "All", value: "all" },
    { label: "Importerr", value: "importerr" },
    { label: "Shopify", value: "shopify" },
    { label: "Amazon", value: "amazon" },
  ];

  const handleQuickFilter = (qf) => {
    if (qf.confirmBankTransferFilter && qf.value === "__confirm_bt__") {
      const isActive = filterOrder.bankTransferConfirmOnly === "true";
      const newFilter = {
        ...filterOrder,
        bankTransferConfirmOnly: isActive ? "" : "true",
        status: isActive ? filterOrder.status : "payment pending",
      };
      setFilterOrder(newFilter);
      setQuery(newFilter);
      return;
    }

    if (qf.ticketFilter && qf.value === "__ticket__") {
      const newTicket = filterOrder.ticket === "true" ? "" : "true";
      const newFilter = { ...filterOrder, ticket: newTicket };
      setFilterOrder(newFilter);
      setQuery(newFilter);
      return;
    }
    const newStatus = filterOrder.status === qf.value ? "" : qf.value;
    const newFilter = { ...filterOrder, status: newStatus };
    setFilterOrder(newFilter);
    setQuery(newFilter);
  };

  return (
    <div className="of-container">
      {/* Row 1: Search + Date + Buttons */}
      <div className="of-main-row">
        <div className="of-search-wrapper">
          <svg className="of-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="of-search-input"
            placeholder="Search Order ID, Name..."
            value={filterOrder.searchFields}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            name="searchFields"
          />
          {filterOrder.searchFields && (
            <button 
              className="of-search-clear" 
              onClick={() => {
                const newFilter = { ...filterOrder, searchFields: "" };
                setFilterOrder(newFilter);
              }}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div className="of-date-wrapper">
          <div className="of-date-field">
            <label>From</label>
            <input
              type="date"
              value={filterOrder.startDate}
              onChange={handleChange}
              name="startDate"
            />
          </div>
          <span className="of-date-sep">–</span>
          <div className="of-date-field">
            <label>To</label>
            <input
              type="date"
              value={filterOrder.endDate}
              onChange={handleChange}
              name="endDate"
            />
          </div>
        </div>

        <button className="of-btn-search" onClick={handleFilter} type="button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          Search
        </button>

        <button 
          className={`of-btn-filters ${showAdvanced ? 'active' : ''}`}
          onClick={() => setShowAdvanced(!showAdvanced)}
          type="button"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
          </svg>
          Filters
          {activeFiltersCount > 0 && <span className="of-badge">{activeFiltersCount}</span>}
        </button>

        {onAddManualOrder && (
          <button 
            className="of-btn-add-manual"
            type="button"
            onClick={onAddManualOrder}
          >
            <i className="fas fa-plus"></i>
            <span>Add Manual Order</span>
          </button>
        )}
      </div>

      {/* Row 2: Quick Filters */}
      <div className="of-quick-row">
        <div className="of-quick-left">
          <span className="of-quick-label">Quick:</span>
          {quickFilters.map((qf) => (
            <button
              key={qf.value}
              className={`of-quick-pill ${
                qf.confirmBankTransferFilter
                  ? (filterOrder.bankTransferConfirmOnly === "true" ? "active" : "")
                  : qf.ticketFilter
                    ? (filterOrder.ticket === "true" ? "active" : "")
                    : (filterOrder.status === qf.value ? "active" : "")
              }`}
              onClick={() => handleQuickFilter(qf)}
              type="button"
            >
              <span className="of-pill-icon">{qf.icon}</span>
              {qf.label}
            </button>
          ))}
          {activeFiltersCount > 0 && (
            <button className="of-clear-all" onClick={clearFilter} type="button">
              ✕ Clear All
            </button>
          )}
        </div>

        <div className="of-payment-status-wrap">
          <label className="of-payment-status-label" htmlFor="of-payment-status-select">
            Payment status
          </label>
          <select
            id="of-payment-status-select"
            className="of-payment-status-select"
            name="paymentStatus"
            value={filterOrder.paymentStatus}
            onChange={(e) => {
              const { name, value } = e.target;
              const next = { ...filterOrder, [name]: value };
              setFilterOrder(next);
              setQuery(next);
            }}
          >
            {paymentOptions.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.name}
              </option>
            ))}
          </select>
        </div>

        {typeof onSourceTabChange === "function" && (
          <div className="of-source-tabs" role="tablist" aria-label="Order source">
            {sourceTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={sourceTab === tab.value}
                className={`of-source-tab ${sourceTab === tab.value ? "active" : ""}`}
                onClick={() => onSourceTabChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Row 3: Advanced Filters (Collapsible) */}
      {showAdvanced && (
        <div className="of-advanced-panel">
          <div className="of-section-title">Order Filters</div>
          <div className="of-advanced-grid">
            <div className="of-field">
              <label>Order Status</label>
              <select value={filterOrder.status} onChange={handleChange} name="status">
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Payment Status</label>
              <select value={filterOrder.paymentStatus} onChange={handleChange} name="paymentStatus">
                {paymentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Marketplace</label>
              <select value={filterOrder.marketplace} onChange={handleChange} name="marketplace">
                {marketplaceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Order Type</label>
              <select value={filterOrder.orderType} onChange={handleChange} name="orderType">
                {orderTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Ticket Status</label>
              <select value={filterOrder.ticket} onChange={handleChange} name="ticket">
                {ticketOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Return Status</label>
              <select value={filterOrder.returnStatus} onChange={handleChange} name="returnStatus">
                {returnOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Failed Order Source</label>
              <select value={filterOrder.fromFailedOrder} onChange={handleChange} name="fromFailedOrder">
                {failedOrderOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>

            <div className="of-field">
              <label>Export Status</label>
              <select value={filterOrder.orderExport} onChange={handleChange} name="orderExport">
                {exportOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="of-section-title">Location Filters</div>
          <div className="of-advanced-grid">
            <div className="of-field">
              <label>State</label>
              <input
                type="text"
                placeholder="Enter state..."
                value={filterOrder.state}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                name="state"
              />
            </div>

            <div className="of-field">
              <label>City</label>
              <input
                type="text"
                placeholder="Enter city..."
                value={filterOrder.city}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                name="city"
              />
            </div>
          </div>

          <div className="of-advanced-actions">
            <button className="of-btn-apply" onClick={handleFilter} type="button">
              Apply Filters
            </button>
            <button className="of-btn-reset" onClick={clearFilter} type="button">
              Reset All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderFilter;
